import { getTimezoneOffset } from 'date-fns-tz';
import * as R from 'ramda';
import { convert12hrTo24hr, convert24hrTo12hr } from '../../../../utils/time-utils';
import { defaultCustomRetention, WtoRule } from '../../wto-form-context';
import {
  formatTimestampOffset,
  parseTimestampCustomRetention,
  setFormattedTime,
  setTimestampTime
} from '../../wto-utils';
import { MILLISECONDS_IN_HOUR, TIMEZONE_MAP } from './rules-form-modal-constants';

export const createCustomRetentionHandlers = (
  newRule: WtoRule,
  setNewRule: (rule: WtoRule) => void,
  savedCustomRetention: ReturnType<typeof parseTimestampCustomRetention>
) => {
  const toggleCustomRetention = (event: boolean) => {
    const newRuleFormObj = R.clone(newRule);
    if (!event) {
      newRuleFormObj.customRetention = {};
    } else {
      const newCustomRetention = { ...defaultCustomRetention };
      newCustomRetention.customRetentionStatus = event;

      newRuleFormObj.customRetention = newCustomRetention;
    }
    setNewRule(newRuleFormObj);
  };

  const handleCustomRetentionDate = (date: string) => {
    const newRuleFormObj = R.clone(newRule);

    const newValues = { ...savedCustomRetention };

    const [year, month, day] = date.split('-');

    newValues.year = year;
    newValues.month = month;
    newValues.day = day;

    const offset =
      getTimezoneOffset(TIMEZONE_MAP[newValues.timezone], new Date(setTimestampTime(newValues))) /
      MILLISECONDS_IN_HOUR;

    newValues.offset = formatTimestampOffset(offset);

    newRuleFormObj.customRetention.startDate = setTimestampTime(newValues);
    newRuleFormObj.customRetention.formattedStartDate = setFormattedTime(newValues);

    setNewRule(newRuleFormObj);
  };

  const handleCustomRetentionTimezone = (timezone: string | null) => {
    if (timezone) {
      const newRuleFormObj = R.clone(newRule);

      const newValues = { ...savedCustomRetention };

      const offset =
        getTimezoneOffset(
          TIMEZONE_MAP[timezone],
          new Date(
            !newRule.customRetention.startDate
              ? setTimestampTime(newValues)
              : newRule.customRetention.startDate
          )
        ) / MILLISECONDS_IN_HOUR;

      newValues.offset = formatTimestampOffset(offset);

      newRuleFormObj.customRetention.time_zone = timezone;
      newRuleFormObj.customRetention.startDate = setTimestampTime(newValues);
      newRuleFormObj.customRetention.formattedStartDate = setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleCustomRetentionTime = (value: string | null, type: 'hour' | 'minute') => {
    if (value) {
      const newRuleFormObj = R.clone(newRule);

      const newValues = { ...savedCustomRetention };

      newValues.hour12 = type === 'hour' ? Number(value).toString() : savedCustomRetention.hour12;
      newValues.minute = type === 'minute' ? value.padStart(2, '0') : savedCustomRetention.minute;

      [newValues.hour24] = convert12hrTo24hr(
        `${newValues.hour12}:${newValues.minute}${savedCustomRetention.period}`
      ).split(':');

      newRuleFormObj.customRetention.startDate = setTimestampTime(newValues);
      newRuleFormObj.customRetention.formattedStartDate = setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  const handleCustomRetentionPeriod = (period: 'am' | 'pm' | null) => {
    if (period) {
      const newRuleFormObj = R.clone(newRule);

      const newValues = { ...savedCustomRetention };

      newValues.period = period;

      if (period === 'am' && newValues.hour24 >= 12) {
        newValues.hour24 = (Number(newValues.hour24) - 12).toString().padStart(2, '0');
      } else if (period === 'pm' && newValues.hour24 < 12) {
        newValues.hour24 = (Number(newValues.hour24) + 12).toString().padStart(2, '0');
      }

      [newValues.hour12] = convert24hrTo12hr(`${newValues.hour24}:${newValues.minute}`).split(':');

      newRuleFormObj.customRetention.startDate = setTimestampTime(newValues);
      newRuleFormObj.customRetention.formattedStartDate = setFormattedTime(newValues);

      setNewRule(newRuleFormObj);
    }
  };

  return {
    toggleCustomRetention,
    handleCustomRetentionDate,
    handleCustomRetentionTimezone,
    handleCustomRetentionTime,
    handleCustomRetentionPeriod
  };
};
